import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import translations from "../../translation"; // Assuming a translations file exists

const PendingApprovalsTable = ({ language = "en" }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingData, setPendingData] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const rowsPerPage = 10;

  const translate = (key) => translations[language]?.[key] || key;

  useEffect(() => {
    fetchPendingData();
  }, []);

  // API to fetch pending announcements
  const fetchPendingData = async () => {
    setIsLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("user_id", user.id);

      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/approval/data`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.success) {
        setPendingData(data.data || []);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching pending data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Approve API call
  const handleApprove = (taskId) => {
    Swal.fire({
      title: translate("confirmApprovalTitle"),
      text: translate("confirmApprovalText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: translate("yesApprove"),
    }).then((result) => {
      if (result.isConfirmed) {
        approveTask(taskId);
      }
    });
  };

  const handleReject = (taskId) => {
    Swal.fire({
      title: translate("confirmRejectionTitle"),
      text: translate("confirmRejectionText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: translate("yesReject"),
    }).then((result) => {
      if (result.isConfirmed) {
        rejectTask(taskId);
      }
    });
  };

  const approveTask = async (taskId) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("id", taskId);
      formData.append("user_id", user.id);
      formData.append("status", "Approved");

      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/approval`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.success) {
        Swal.fire(translate("approvedTitle"), translate("approvedMessage"), "success");
        fetchPendingData();
      } else {
        Swal.fire(translate("errorTitle"), data.message, "error");
      }
    } catch (error) {
      console.error("Error approving task:", error);
      Swal.fire(translate("errorTitle"), translate("errorApprovalMessage"), "error");
    }
  };

  const rejectTask = async (taskId) => {
    try {
      const { value: reason } = await Swal.fire({
        title: translate("provideRejectionReasonTitle"),
        input: "textarea",
        inputLabel: translate("reasonLabel"),
        inputPlaceholder: translate("rejectionReasonPlaceholder"),
        inputAttributes: {
          "aria-label": translate("rejectionReasonPlaceholder"),
        },
        showCancelButton: true,
        confirmButtonText: translate("submit"),
        cancelButtonText: translate("cancel"),
      });

      if (!reason) {
        Swal.fire(translate("cancelledTitle"), translate("rejectionCancelledMessage"), "info");
        return;
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("id", taskId);
      formData.append("user_id", user.id);
      formData.append("status", "Rejected");
      formData.append("reject_reason", reason);

      const response = await fetch(
        `${process.env.REACT_APP_CHECK_USER_API}/api/approval`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.success) {
        Swal.fire(translate("rejectedTitle"), translate("rejectedMessage"), "success");
        fetchPendingData();
      } else {
        Swal.fire(translate("errorTitle"), data.message, "error");
      }
    } catch (error) {
      console.error("Error rejecting task:", error);
      Swal.fire(translate("errorTitle"), translate("errorRejectionMessage"), "error");
    }
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = pendingData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(pendingData.length / rowsPerPage);

  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const handleView = (task) => {
    setSelectedTask(task);
    setModalShow(true);
  };

  return (
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {translate("pendingAnnouncementsTitle")}
        </h3>
      </div>
      {isLoading ? (
        <div className="text-center py-6 text-gray-600">{translate("loading")}</div>
      ) : pendingData.length === 0 ? (
        <div className="text-center py-6 text-gray-600">
          {translate("noPendingAnnouncements")}
        </div>
      ) : (
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("title")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("submittedBy")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("date")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("status")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {translate("actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-4 text-sm text-gray-900">{row.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {row.fullname || translate("notAvailable")}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(row.sent_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          row.status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : row.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {translate(row.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => handleView(row)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-custom hover:text-custom-dark border border-custom rounded-button mr-2"
                      >
                        {translate("view")}
                      </button>
                      { /* row.status === "Pending" && (
                        <>
                          <button
                            onClick={() => handleApprove(row.id)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-button mr-2"
                          >
                            {translate("approve")}
                          </button>
                          <button
                            onClick={() => handleReject(row.id)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-button"
                          >
                            {translate("reject")}
                          </button>
                        </>
                      ) */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-between items-center px-6 py-3 space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                  currentPage === 1
                    ? "bg-gray-300 text-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {translate("prev")}
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                  currentPage === totalPages
                    ? "bg-gray-300 text-gray-500"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {translate("next")}
              </button>
          </div>
        </div>
      )}

      <Modal size="lg" show={modalShow} onHide={() => setModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translate("taskDetailsTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{translate("title")}:</strong> {selectedTask?.title || "N/A"}
          </p>
          <p>
            <strong>{translate("submittedBy")}:</strong> {selectedTask?.username || "N/A"}
          </p>
          <p>
            <strong>{translate("announcement")}:</strong> {selectedTask?.content || "N/A"}
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PendingApprovalsTable;
